import assetLibraryClient from '@/api/assetLibraryClient';

import { isAssetType, type AssetType } from './constants/assetTypes';
import type { CopyElement } from './constants/metadataFields';

/**
 * Typed contract for the Asset Library backend.
 *
 * Deliberately landed complete rather than grown per-feature: the upload flow
 * and the asset grid are built in parallel on separate branches, and both need
 * these types. Landing the whole surface once means those branches only ever
 * consume this file, so they never conflict in it.
 *
 * Every interface mirrors `apps/asset-library/backend/src/api/schemas.py`. If
 * you change one, change the other.
 */

// ---------------------------------------------------------------- Assets

/** Mirrors the backend's status constants (`database/models.py`). */
export type AssetStatus = 'uploading' | 'staged' | 'published' | 'archived';

/** Lives in `constants/metadataFields.ts` so that leaf imports nothing; re-exported here. */
export type { CopyElement };

/** One recorded finding from the static reference check. */
export interface OlaReportIssue {
  code: string;
  message?: string;
}

/**
 * What the backend found when it checked the banner at confirm time.
 *
 * Warnings never block an upload or a publish: real Hoxton templates carry
 * conditional and dead references routinely. A banner that cannot be served
 * at all is rejected at confirm and never gets a report.
 */
export interface OlaReport {
  warnings: OlaReportIssue[];
  /** Absolute URLs the banner loads, GSAP and Enabler among them. Not fetched. */
  external_refs: string[];
}

export interface AssetResponse {
  id: string;
  batch_id: string;
  status: AssetStatus;
  filename_original: string;
  storage_path: string;
  content_md5: string | null;
  width: number | null;
  height: number | null;
  /**
   * Which of the four kinds this is (see `constants/assetTypes.ts`). The
   * backend still serves the older `media_kind` / `format` / `is_source_image`
   * encoding of the same fact; nothing here reads it, so it is not typed.
   */
  asset_type: AssetType;
  /** ISO date (YYYY-MM-DD). Displayed DD/MM/YYYY. */
  expiry_rights: string | null;
  /** Three independent lists; nothing is derived between them. */
  nameplate: string[] | null;
  business_unit: string[] | null;
  carline_code: string[] | null;
  tier: string[] | null;
  ai_indicator: string | null;
  copy_elements: CopyElement[] | null;
  platform_rights: string[] | null;
  /**
   * Read-only, derived server-side — never sent on an update. A master image
   * runs in its format's channel (`VocabularyResponse.format_types`); an OLA
   * follows `platform_rights` via the shared taxonomy. Null when there is
   * nothing to derive from, and null when the rights span more than one
   * channel (Meta and Google together have no single answer).
   */
  channel: string | null;
  /** The channels the licence covers (digital / print / broadcast). */
  channel_rights: string[] | null;
  /**
   * The markets the licence covers: ISO country codes and/or region tokens
   * (`pan_euro`, `eu5`, `eso`), stored as picked. What a region expands to is
   * in `VocabularyResponse.regions`.
   */
  country_rights: string[] | null;
  /**
   * Read-only, derived server-side: every country `country_rights` reaches,
   * regions expanded with today's members — never sent on an update. Empty
   * when there are no rights yet.
   */
  countries_covered: string[];
  source_agency: string | null;
  /** Source images only: true or false on one, null on every other kind. */
  is_lifestyle_image: boolean | null;
  domain: string | null;
  /**
   * A master image's ad format and the ratio it was cut at. A film has the
   * size alone, as its declared aspect ratio (`film_format_sizes`).
   */
  format_type: string | null;
  format_size: string | null;
  /** Films only: one of `film_durations`, declared at upload, never read from the video. */
  duration_seconds: number | null;
  /**
   * A film's script (.xlsx), by the name it was uploaded as; null when it has
   * none, and on every other kind. Changes only while the film is staged.
   */
  script_filename: string | null;
  legacy_fcv_id: string | null;
  /**
   * Source image lineage. An asset is exactly one of:
   *  - a source image, carrying a `source_image_code` (`SI-000042`) and no sources;
   *  - a derivative, cut from one or more source images and carrying no code.
   *
   * `source_image_ids` and `source_image_codes` are ordered, and the order is
   * the one the links were made in. The codes list can be shorter than the ids
   * list — a source image whose code has not been allocated yet contributes
   * none — so do not pair them positionally; index by `source_image_ids`.
   */
  source_image_code: string | null;
  source_image_ids: string[];
  source_image_codes: string[];
  /**
   * A carousel's stills in running order; null for every other kind. The
   * asset's `source_image_ids` are derived from these and always name the
   * same sources.
   */
  frames?: AssetFrameResponse[] | null;
  /**
   * OLA banners only, null otherwise; the file to point the iframe at.
   * See `Asset.ola_entry_path` in models.py for how it relates to `storage_path`.
   */
  ola_entry_path: string | null;
  ola_report: OlaReport | null;
  /**
   * Master-asset fields. `market` and `language` are server-set (EU/EN on
   * OLA masters) and never sent by the client; the rest are edited like any
   * other metadata. `campaign_name` is publish-required for OLA.
   */
  campaign_name: string | null;
  creative_variation: string | null;
  market: string | null;
  language: string | null;
  production_ticket_ref: string | null;
  briefing_form_ref: string | null;
  notes: string | null;
  aspect_ratio: string | null;
  uploaded_by: string;
  /** ISO 8601 datetime. */
  created_at: string;
  updated_at: string;
}

/** One still of a carousel, as returned inside its asset. Mirrors `AssetFrameResponse`. */
export interface AssetFrameResponse {
  id: string;
  /** 0-based running order. */
  position: number;
  filename_original: string;
  width: number | null;
  height: number | null;
  source_image_id: string | null;
  source_image_code: string | null;
  copy_elements: CopyElement[] | null;
}

export interface ListAssetsResponse {
  assets: AssetResponse[];
  total: number;
}

// ---------------------------------------------------------------- Batches

export interface BatchResponse {
  id: string;
  label: string | null;
  created_by: string;
  created_at: string;
  asset_count: number;
  staged_count: number;
  published_count: number;
}

export interface ListBatchesResponse {
  batches: BatchResponse[];
  total: number;
}

// ---------------------------------------------------------------- Upload

export interface UploadFileRequest {
  filename: string;
  content_type: string;
}

/**
 * Metadata entered in the upload form, applied to every asset in the batch.
 *
 * Same field set as `AssetUpdateFields`. `source_image_ids` links the whole
 * batch to the same source images; on the OLA route the backend also copies the
 * derived fields (nameplate, carline, business unit, tier, expiry, agency, AI indicator) onto
 * every banner, resolving them where the sources disagree. Per-file differences
 * are refined afterwards in the details drawer, which is why there is no
 * per-file variant here.
 */
export interface AssetUploadMetadata {
  /** ISO date (YYYY-MM-DD). */
  expiry_rights?: string;
  nameplate?: string[];
  business_unit?: string[];
  carline_code?: string[];
  tier?: string[];
  ai_indicator?: string;
  copy_elements?: CopyElement[];
  platform_rights?: string[];
  channel_rights?: string[];
  country_rights?: string[];
  source_agency?: string;
  /** Source images only. Left out, false at upload and unchanged on an edit; never null. */
  is_lifestyle_image?: boolean;
  domain?: string;
  /**
   * A master's size needs a type, and must be one that type comes in. A
   * film's stands alone and is one of `film_format_sizes`.
   */
  format_type?: string;
  format_size?: string;
  /** Films only: one of `film_durations`. */
  duration_seconds?: number;
  legacy_fcv_id?: string;
  campaign_name?: string;
  creative_variation?: string;
  production_ticket_ref?: string;
  briefing_form_ref?: string;
  notes?: string;
  /** The source images every asset in the batch derives from. Optional; linkable later. */
  source_image_ids?: string[];
}

export interface CreateBatchRequest {
  /**
   * Which kind the whole batch is; OLA banners go through `createOlaBatch`.
   * Every file must be of that kind's media kind — the backend refuses a video
   * in a batch of master images rather than quietly making it a film. Source
   * images each get their own SI code. The kind is final: a mis-classified
   * upload is deleted and uploaded again.
   */
  asset_type: Exclude<AssetType, 'ola'>;
  files: UploadFileRequest[];
  label?: string | null;
  metadata?: AssetUploadMetadata | null;
}

/**
 * Where the client PUTs one file.
 *
 * `content_type` is echoed back because the signed URL is bound to it: the PUT
 * must send exactly this Content-Type header or GCS rejects the signature.
 */
export interface UploadTarget {
  asset_id: string;
  filename: string;
  upload_url: string;
  content_type: string;
}

export interface CreateBatchResponse {
  batch_id: string;
  uploads: UploadTarget[];
  expires_in_seconds: number;
}

/** One image for upload with JSON, carrying the content of the JSON paired with it. */
export interface SidecarFileRequest extends UploadFileRequest {
  sidecar: Record<string, unknown> | null;
}

/** An image whose JSON was read, and what it will be uploaded as. */
export interface SidecarAcceptedFile {
  filename: string;
  asset_type: AssetType;
  metadata: AssetUploadMetadata;
}

/** An image that will not be uploaded, with the reason its JSON was refused. */
export interface SidecarRejectedFile {
  filename: string;
  reason: string;
}

export interface SidecarCheckResponse {
  accepted: SidecarAcceptedFile[];
  rejected: SidecarRejectedFile[];
  /**
   * More rejected files than this, counting the ones the browser rejected
   * before asking, and nothing in the drop is uploaded.
   */
  max_rejected: number;
}

export interface SidecarUploadRequest {
  files: SidecarFileRequest[];
  label?: string | null;
}

/**
 * Result of confirming one upload.
 *
 * `duplicate_of` is advisory only — matching bytes never block an upload, they
 * just let the UI warn that the same file already exists.
 */
export interface ConfirmUploadResponse {
  asset: AssetResponse;
  duplicate_of: string[];
}

// ------------------------------------------------------------------ OLA

/**
 * One member of a banner. No `content_type`: the server derives it from the
 * extension, because browsers report `.woff2` as `''` and `.js` inconsistently.
 */
export interface OlaMemberRequest {
  /** Relative to the ad root, forward-slashed. Stored verbatim. */
  relpath: string;
  size: number;
}

export interface OlaAdRequest {
  name: string;
  entry_relpath: string;
  /** Required — an ad with no fallback still is refused. */
  poster_relpath: string;
  files: OlaMemberRequest[];
}

export interface CreateOlaBatchRequest {
  ads: OlaAdRequest[];
  label?: string | null;
  metadata?: AssetUploadMetadata | null;
}

export interface OlaUploadTarget {
  relpath: string;
  upload_url: string;
  /** The exact Content-Type the URL was signed with; the PUT must match it. */
  content_type: string;
}

export interface OlaAdTarget {
  asset_id: string;
  name: string;
  uploads: OlaUploadTarget[];
}

export interface CreateOlaBatchResponse {
  batch_id: string;
  ads: OlaAdTarget[];
  expires_in_seconds: number;
}

// ------------------------------------------------------------- Carousels

/** One still of a carousel, in running order. Mirrors `CarouselFrameRequest`. */
export interface CarouselFrameRequest {
  filename: string;
  content_type: string;
  size: number;
  /** Required: each frame is cut from exactly one published source image. */
  source_image_id: string;
  copy_elements: CopyElement[];
}

export interface CarouselRequest {
  name: string;
  frames: CarouselFrameRequest[];
}

export interface CreateCarouselBatchRequest {
  carousels: CarouselRequest[];
  label?: string | null;
  /** Carousel-level only: copy and source images are per frame and refused here. */
  metadata?: AssetUploadMetadata | null;
}

export interface CarouselFrameTarget {
  frame_id: string;
  position: number;
  filename: string;
  upload_url: string;
  /** The exact Content-Type the URL was signed with; the PUT must match it. */
  content_type: string;
}

export interface CarouselTarget {
  asset_id: string;
  name: string;
  frames: CarouselFrameTarget[];
}

export interface CreateCarouselBatchResponse {
  batch_id: string;
  carousels: CarouselTarget[];
  expires_in_seconds: number;
}

export interface OlaToken {
  asset_id: string;
  token: string;
}

/** `missing` holds ids that are not confirmed OLA assets, reported not raised. */
export interface OlaTokenResponse {
  tokens: OlaToken[];
  missing: string[];
  expires_in_seconds: number;
}

// ---------------------------------------------------------- Bulk edit

/**
 * The subset of an asset the bulk editor may change.
 *
 * Fields omitted from the object are left untouched by the backend, which is
 * why this is a partial rather than a full asset: sending an explicit null
 * where you meant "don't change" would wipe the column on every selected row.
 * The publish-required fields (everything except `source_image_ids`) cannot be
 * cleared — the backend 422s an explicit null or empty list on them.
 */
export interface AssetUpdateFields {
  /** ISO date (YYYY-MM-DD). */
  expiry_rights?: string;
  nameplate?: string[];
  business_unit?: string[];
  carline_code?: string[];
  tier?: string[];
  ai_indicator?: string;
  copy_elements?: CopyElement[];
  platform_rights?: string[];
  channel_rights?: string[];
  country_rights?: string[];
  source_agency?: string;
  /** Source images only. Left out, false at upload and unchanged on an edit; never null. */
  is_lifestyle_image?: boolean;
  domain?: string;
  /**
   * A master's are always sent as a pair: the backend writes the selection
   * without reading it, so half a pair is refused rather than matched against
   * whatever each row stores. A film's size is sent alone. Neither can be
   * cleared.
   */
  format_type?: string;
  format_size?: string;
  /** Films only: one of `film_durations`. Cannot be cleared. */
  duration_seconds?: number;
  /** Optional; null clears it (not publish-required, not clear-guarded). */
  legacy_fcv_id?: string | null;
  campaign_name?: string;
  creative_variation?: string;
  production_ticket_ref?: string;
  briefing_form_ref?: string;
  notes?: string;
  /**
   * The source images this asset is cut from, in the order they should stack.
   *
   * Replaces the whole set rather than appending, so `[]` clears the lineage —
   * that is the only way to undo a bad link. Only meaningful on assets that are
   * not themselves source images; the backend skips those rather than failing
   * the request. Whether an asset is a source image is fixed at upload.
   */
  source_image_ids?: string[];
}

export interface UpdateAssetsRequest {
  asset_ids: string[];
  fields: AssetUpdateFields;
  /**
   * Copy the linked source images' metadata down onto the assets being linked.
   *
   * Opt-in, and off by default, because the copy overwrites values nobody
   * mentioned in the request. Only set it once the user has been asked — see
   * the overwrite dialog in `SourceImageSection`. Ignored unless
   * `fields.source_image_ids` names at least one source image.
   */
  inherit_metadata?: boolean;
}

/** One asset a bulk operation declined to touch, and why. */
export interface SkippedAsset {
  asset_id: string;
  reason: string;
  /**
   * Set only on skips the client is expected to act on, rather than display.
   *
   * `reason` is prose for a human and is reworded freely; branching on its text
   * breaks the moment someone improves the sentence, and does so silently. Match
   * this instead — see `SKIP_CASCADE_UNCONFIRMED`.
   */
  code?: string | null;
  /**
   * Only on a `SKIP_CASCADE_UNCONFIRMED` skip: the assets the confirmed retry
   * would take with it, so the confirmation can name them. The grid cannot
   * work this out itself; it only holds the current page.
   */
  cascade?: CascadeAsset[] | null;
}

/** One asset a source image delete would take with it. */
export interface CascadeAsset {
  asset_id: string;
  filename: string;
  status: string;
}

/**
 * The delete was refused only because the cascade had not been agreed to.
 *
 * Mirrors `SKIP_CASCADE_UNCONFIRMED` in
 * `apps/asset-library/backend/src/api/assets.py`. The only skip that is a
 * question rather than a verdict: retrying the same ids with `confirm_cascade`
 * succeeds, where every other skip would refuse again.
 */
export const SKIP_CASCADE_UNCONFIRMED = 'cascade_unconfirmed';

/**
 * Bulk operations are partial-success: one unpublishable row must not fail the
 * other 499, so callers must read `skipped` rather than assuming everything in
 * the request succeeded.
 */
export interface BulkResultResponse {
  updated: string[];
  skipped: SkippedAsset[];
}

export interface PublishAssetsRequest {
  asset_ids: string[];
}

export interface DeleteAssetsRequest {
  asset_ids: string[];
  /**
   * Also delete the assets cut from any source image being deleted.
   *
   * Off by default, and the backend skips rather than cascades without it,
   * returning a reason carrying the count. That skip is the prompt: ask with
   * the real number, then retry with this set. Sending it up front would turn
   * the guard back into the silent cascade it exists to prevent.
   */
  confirm_cascade?: boolean;
}

// ------------------------------------------------------------ Display URLs

export interface AssetUrl {
  asset_id: string;
  /** The original, for the detail view. */
  url: string;
  /** A small still for the grid; null for an asset that has none yet. */
  thumbnail_url?: string | null;
}

/**
 * Signed display URLs, batched.
 *
 * `missing` holds ids that produced no URL — unknown, or still `uploading` and
 * so without a stored object to point at. Reported rather than raised so one
 * bad id doesn't blank the whole grid.
 */
export interface AssetUrlsResponse {
  urls: AssetUrl[];
  missing: string[];
  expires_in_seconds: number;
}

// ---------------------------------------------------------------- Requests

export interface ListAssetsParams {
  batch_id?: string;
  status?: AssetStatus;
  /** Comma-separated; an asset matches when its list holds any of them. */
  nameplate?: string;
  carline_code?: string;
  business_unit?: string;
  tier?: string;
  /** Comma-separated kinds. `source_image` alone is what the source image picker reads. */
  asset_type?: string;
  /** Comma-separated film lengths in seconds; matches films only. */
  duration_seconds?: string;
  /** Comma-separated film sizes; matches films only. */
  film_format_size?: string;
  reference_id?: string;
  /**
   * Comma-separated ids: an explicit set rather than a page.
   *
   * For reading the *current* values of a selection you already hold, when the
   * cached copy is not good enough to act on — the source image overwrite
   * warning re-reads the rows it is about to replace this way. Malformed ids
   * are a 422, and the server sizes the page to the number of ids so a large
   * selection cannot come back quietly truncated.
   */
  ids?: string;
  limit?: number;
  offset?: number;
}

export interface CampaignsResponse {
  /** Distinct campaign names, most used first. */
  campaigns: string[];
}

export interface ListBatchesParams {
  limit?: number;
  offset?: number;
}

/**
 * Open a batch and reserve an upload URL per file.
 *
 * Takes a signal because signing 500 URLs is slow enough to cancel during:
 * without it a user who closes the modal here still gets a batch row and 500
 * `uploading` asset rows that nothing will ever claim or reap.
 */
export async function createBatch(
  body: CreateBatchRequest,
  signal?: AbortSignal,
): Promise<CreateBatchResponse> {
  const response = await assetLibraryClient.post<CreateBatchResponse>('/batches', body, { signal });
  return response.data;
}

/**
 * Read each image's JSON, without creating anything.
 *
 * Upload with JSON shows this to the uploader before they commit. The backend
 * re-runs the same check on `createSidecarBatch`.
 */
export async function checkSidecars(
  files: SidecarFileRequest[],
  signal?: AbortSignal,
): Promise<SidecarCheckResponse> {
  const response = await assetLibraryClient.post<SidecarCheckResponse>(
    '/sidecar-uploads/check',
    { files },
    { signal },
  );
  return response.data;
}

/**
 * Open a batch of source images, each with the metadata from its own JSON.
 * All or nothing: one file the server no longer accepts refuses the batch (422).
 */
export async function createSidecarBatch(
  body: SidecarUploadRequest,
  signal?: AbortSignal,
): Promise<CreateBatchResponse> {
  const response = await assetLibraryClient.post<CreateBatchResponse>('/sidecar-uploads', body, {
    signal,
  });
  return response.data;
}

/** Confirm one file once its bytes have landed in GCS. */
export async function confirmUpload(
  assetId: string,
  signal?: AbortSignal,
): Promise<ConfirmUploadResponse> {
  const response = await assetLibraryClient.post<ConfirmUploadResponse>(
    `/assets/${assetId}/confirm`,
    undefined,
    { signal },
  );
  return response.data;
}

/** Where to PUT a film's script, and the id to confirm it by. */
export interface ScriptUploadTarget {
  upload_id: string;
  upload_url: string;
  /** The signed URL is bound to it: the PUT must send exactly this Content-Type. */
  content_type: string;
}

/** Ask to upload a script for a staged film. Refused (400) unless the name ends in .xlsx. */
export async function requestScriptUpload(
  assetId: string,
  filename: string,
  signal?: AbortSignal,
): Promise<ScriptUploadTarget> {
  const response = await assetLibraryClient.post<ScriptUploadTarget>(
    `/assets/${assetId}/script`,
    { filename },
    { signal },
  );
  return response.data;
}

/** Make the PUT file the film's script, replacing any it had. Returns the film. */
export async function confirmScriptUpload(
  assetId: string,
  uploadId: string,
  filename: string,
  signal?: AbortSignal,
): Promise<AssetResponse> {
  const response = await assetLibraryClient.post<AssetResponse>(
    `/assets/${assetId}/script/confirm`,
    { upload_id: uploadId, filename },
    { signal },
  );
  return response.data;
}

/** Remove a staged film's script. Returns the film. */
export async function deleteScript(assetId: string): Promise<AssetResponse> {
  const response = await assetLibraryClient.delete<AssetResponse>(`/assets/${assetId}/script`);
  return response.data;
}

/**
 * Open a batch of OLA banners, one asset per ad.
 *
 * Separate from `createBatch` rather than a flag on it: an ad is a folder with
 * an entry file and a poster, and every member's path has to survive intact,
 * none of which the single-file shape has anywhere to put.
 */
export async function createOlaBatch(
  body: CreateOlaBatchRequest,
  signal?: AbortSignal,
): Promise<CreateOlaBatchResponse> {
  const response = await assetLibraryClient.post<CreateOlaBatchResponse>('/ola-batches', body, {
    signal,
  });
  return response.data;
}

/**
 * Confirm one banner once every one of its files has landed.
 *
 * This is where the folder is checked and promoted: a call before all the PUTs
 * finish 400s naming what is missing, and leaves the asset re-confirmable.
 */
export async function confirmOlaUpload(
  assetId: string,
  signal?: AbortSignal,
): Promise<ConfirmUploadResponse> {
  const response = await assetLibraryClient.post<ConfirmUploadResponse>(
    `/assets/${assetId}/ola/confirm`,
    undefined,
    { signal },
  );
  return response.data;
}

/**
 * Open a batch of carousels: one asset per carousel, a signed URL per frame.
 *
 * All-or-nothing: a carousel with the wrong frame count, a frame that is not a
 * JPEG, PNG or WebP, or a frame whose source image is not published fails the
 * whole request, and nothing is created.
 */
export async function createCarouselBatch(
  body: CreateCarouselBatchRequest,
  signal?: AbortSignal,
): Promise<CreateCarouselBatchResponse> {
  const response = await assetLibraryClient.post<CreateCarouselBatchResponse>(
    '/carousel-batches',
    body,
    { signal },
  );
  return response.data;
}

/**
 * Confirm one carousel once every frame has landed.
 *
 * Checks each frame is the image it was declared as and that every frame is
 * the same pixel size; a 400 names the frame at fault and leaves the carousel
 * re-confirmable.
 */
export async function confirmCarouselUpload(
  assetId: string,
  signal?: AbortSignal,
): Promise<ConfirmUploadResponse> {
  const response = await assetLibraryClient.post<ConfirmUploadResponse>(
    `/assets/${assetId}/carousel/confirm`,
    undefined,
    { signal },
  );
  return response.data;
}

/**
 * Capability tokens for viewing banners, batched like `getAssetUrls`.
 *
 * POST with a body rather than a query string: 200 ids is about 7 KB of URL.
 * Costs no signing round trip, so it is cheap to ask for a whole page at once.
 */
export async function getOlaTokens(assetIds: string[]): Promise<OlaTokenResponse> {
  const response = await assetLibraryClient.post<OlaTokenResponse>('/assets/ola-tokens', {
    ids: assetIds,
  });
  return response.data;
}

/** Campaign names already in the library, for the campaign input's suggestions. */
export async function listCampaigns(): Promise<CampaignsResponse> {
  const response = await assetLibraryClient.get<CampaignsResponse>('/assets/campaigns');
  return response.data;
}

// ------------------------------------------------------------- vocabulary

export interface VocabularyOption {
  value: string;
  label: string;
}

/** A size a format type comes in, and the channel (`social` / `search`) that pair runs in. */
export interface FormatSizeOption extends VocabularyOption {
  channel: string;
}

/**
 * A media-plan row a format type is briefed against: Content Tracker's
 * `format` and `platform`, lowercased. A null platform takes any platform.
 */
export interface FormatMediaPlanRow {
  format: string;
  platform: string | null;
}

/** A master image format type and the sizes it comes in, in picker order. */
export interface FormatTypeOption extends VocabularyOption {
  sizes: FormatSizeOption[];
  media_plan_rows: FormatMediaPlanRow[];
  /** False where a row's size is not the master's (a YouTube row's is its video's). */
  briefed_by_size: boolean;
}

/** A region preset and the country codes it expands to. */
export interface RegionOption extends VocabularyOption {
  countries: string[];
}

/**
 * Every controlled vocabulary, from the backend's `src/core/vocabulary.py`.
 *
 * The frontend keeps no copy of any of these lists. `constants/taxonomy.ts`
 * holds only UI concerns (filter presets, publish rules);
 * anything a user picks from here comes from this endpoint.
 */
export interface VocabularyResponse {
  nameplates: VocabularyOption[];
  carline_codes: VocabularyOption[];
  business_units: VocabularyOption[];
  tiers: VocabularyOption[];
  ai_indicators: VocabularyOption[];
  source_agencies: VocabularyOption[];
  domains: VocabularyOption[];
  channel_rights: VocabularyOption[];
  format_types: FormatTypeOption[];
  /** A film's aspect ratios. No type goes with them, so no channel. */
  film_format_sizes: VocabularyOption[];
  /** A film's lengths, in seconds. */
  film_durations: number[];
  countries: VocabularyOption[];
  regions: RegionOption[];
}

// ------------------------------------------------------------ Permissions

/**
 * Mirrors `PermissionsResponse`. Asset Library's own matrix
 * (`services/permission_service.py`), not Content Tracker's: the same role
 * may do different things in each app, so the two are never mixed.
 */
export interface AssetLibraryPermissions {
  can_view_staged: boolean;
  can_view_source_images: boolean;
  can_manage_assets: boolean;
  can_delete_assets: boolean;
  can_edit_published: boolean;
  can_brief: boolean;
  can_manage_all_briefs: boolean;
  /** Other users' requested briefs show in My Briefs (never their drafts). */
  can_view_all_briefs: boolean;
  /** Other users' requested briefs in this user's markets show in My Briefs. */
  can_view_market_briefs: boolean;
  can_download_assets: boolean;
  /** For display only. Gate on the flags above, never on this. */
  role: string;
}

export async function getMyPermissions(): Promise<AssetLibraryPermissions> {
  const response = await assetLibraryClient.get<AssetLibraryPermissions>('/me/permissions');
  return response.data;
}

export async function getVocabulary(): Promise<VocabularyResponse> {
  const response = await assetLibraryClient.get<VocabularyResponse>('/vocabulary');
  return response.data;
}

export async function listBatches(params: ListBatchesParams = {}): Promise<ListBatchesResponse> {
  const response = await assetLibraryClient.get<ListBatchesResponse>('/batches', { params });
  return response.data;
}

/**
 * Thrown by `getAsset` for a row of a kind this build does not know.
 *
 * Its own class so the overlay can tell "unsupported here" from a 404 if it
 * ever wants to; today both end in the same "could not open" message.
 */
export class UnknownAssetTypeError extends Error {
  constructor(
    readonly assetId: string,
    readonly assetType: unknown,
  ) {
    super(`Asset ${assetId} has a type this build does not know: ${String(assetType)}`);
    this.name = 'UnknownAssetTypeError';
  }
}

/**
 * The rows whose `asset_type` this build knows.
 *
 * `asset_type` is typed `AssetType` everywhere past this file, and the grid,
 * the publish gate and the metadata form all index `ASSET_TYPES` by it with
 * no guard, so a value outside the four would throw inside a render and take
 * the whole page down rather than one card. The backend's CHECK constraint
 * means that cannot happen against a backend of the same vintage; it can
 * happen when a fifth kind ships backend-first, which is how this series
 * itself is deployed. Unknown rows are dropped here, once, with a warning, so
 * everything downstream keeps the type it was promised.
 *
 * `total` on the list response is left as the server reported it: it drives
 * paging, and hiding a row is not the same as the row not existing.
 */
export function knownAssets(assets: readonly AssetResponse[]): AssetResponse[] {
  const known = assets.filter((asset) => isAssetType(asset.asset_type));
  if (known.length !== assets.length) {
    const dropped = assets.filter((asset) => !isAssetType(asset.asset_type));
    console.warn(
      `[asset-library] hid ${dropped.length} asset(s) of a type this build does not know:`,
      dropped.map((asset) => `${asset.id} (${String(asset.asset_type)})`),
    );
  }
  return known;
}

export async function listAssets(params: ListAssetsParams = {}): Promise<ListAssetsResponse> {
  const response = await assetLibraryClient.get<ListAssetsResponse>('/assets', { params });
  return { ...response.data, assets: knownAssets(response.data.assets) };
}

/**
 * One asset by id.
 *
 * Exists for permalinks. The grid reads a page at a time, so an asset someone
 * shared a link to may simply not be in the page the recipient loads — a link
 * to the 240th published asset opens on a first page that stops at 200. The
 * overlay falls back to this when the id in the URL isn't in the list it has.
 *
 * 404s for an unknown id, and for one still `uploading`. Rejects with
 * `UnknownAssetTypeError` for a kind this build does not know, for the reason
 * given on `knownAssets`: the caller is about to render it.
 */
export async function getAsset(assetId: string): Promise<AssetResponse> {
  const response = await assetLibraryClient.get<AssetResponse>(`/assets/${assetId}`);
  if (!isAssetType(response.data.asset_type)) {
    throw new UnknownAssetTypeError(assetId, response.data.asset_type);
  }
  return response.data;
}

export async function updateAssets(body: UpdateAssetsRequest): Promise<BulkResultResponse> {
  const response = await assetLibraryClient.patch<BulkResultResponse>('/assets', body);
  return response.data;
}

export async function publishAssets(body: PublishAssetsRequest): Promise<BulkResultResponse> {
  const response = await assetLibraryClient.post<BulkResultResponse>('/assets/publish', body);
  return response.data;
}

/**
 * Permanently delete assets and the objects behind them.
 *
 * POST, not DELETE: this is a bulk operation over a body of ids and shares
 * publish's partial-success response, so it shares its shape too.
 */
export async function deleteAssets(body: DeleteAssetsRequest): Promise<BulkResultResponse> {
  const response = await assetLibraryClient.post<BulkResultResponse>('/assets/delete', body);
  return response.data;
}

/**
 * Change one frame of an uploaded carousel. Mirrors `UpdateFrameRequest`: send
 * either or both; a source is replaced, never removed, and `[]` clears copy.
 */
export interface UpdateFrameRequest {
  source_image_id?: string;
  copy_elements?: CopyElement[];
}

/**
 * Save one frame's source image and/or copy. The backend applies the upload's
 * rules and answers with the whole carousel, frames re-read.
 */
export async function updateFrame(
  assetId: string,
  frameId: string,
  body: UpdateFrameRequest,
): Promise<AssetResponse> {
  const response = await assetLibraryClient.patch<AssetResponse>(
    `/assets/${assetId}/frames/${frameId}`,
    body,
  );
  return response.data;
}

/** A display URL for one frame of a carousel. Mirrors `FrameUrl`. */
export interface FrameUrl {
  frame_id: string;
  position: number;
  url: string;
}

export interface FrameUrlsResponse {
  urls: FrameUrl[];
  expires_in_seconds: number;
}

/**
 * Signed display URLs for every frame of one carousel, in running order.
 *
 * Only the detail view asks: the grid's `getAssetUrls` signs frame 1, which is
 * all a card shows. Another kind answers with an empty list.
 */
export async function getFrameUrls(assetId: string): Promise<FrameUrlsResponse> {
  const response = await assetLibraryClient.get<FrameUrlsResponse>(
    `/assets/${assetId}/frames/urls`,
  );
  return response.data;
}

/**
 * Signed display URLs for many assets at once.
 *
 * Batched deliberately: each URL costs a signing round trip on the backend, so
 * a grid that fetched one at a time would issue one request and one signature
 * per thumbnail. Pass the whole visible page.
 *
 * Returns an empty result for an empty selection rather than calling the API.
 */
export async function getAssetUrls(assetIds: string[]): Promise<AssetUrlsResponse> {
  if (assetIds.length === 0) {
    return { urls: [], missing: [], expires_in_seconds: 0 };
  }
  const response = await assetLibraryClient.get<AssetUrlsResponse>('/assets/urls', {
    params: { ids: assetIds.join(',') },
  });
  return response.data;
}

// ------------------------------------------------------------- Downloads

/** A downloaded file: its bytes, and the name the backend gave it. */
export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

/** Called as bytes arrive: the fraction done, or null when the total is unknown. */
export type DownloadProgress = (fraction: number | null) => void;

/**
 * The name from a `Content-Disposition` header, or null if it carries none.
 *
 * The backend builds the name (`SI-000042_Puma-Gen-E_2048x2048.png`) and
 * guarantees it is ASCII, so the plain `filename="..."` form is all it sends.
 * Read from the header rather than rebuilt here, so the naming rule lives in
 * one place: the code at the front is what links a finished master asset
 * back to its source image, and two copies of that rule would drift.
 */
export function filenameFromDisposition(header: string | undefined): string | null {
  const match = header ? /filename="([^"]+)"/.exec(header) : null;
  return match ? match[1] : null;
}

/**
 * The original bytes of one published source image.
 *
 * Fetched through this client, not opened as a link: requests are authorised
 * by bearer token, which a plain navigation does not send. The cost is that
 * the file passes through memory as a blob before it is saved, which is fine
 * for a single image.
 *
 * No timeout. The client's 60 seconds is sized for JSON; a large file on a
 * slow connection legitimately takes longer, and cutting it off would look
 * like a failure the user can only answer by trying again.
 *
 * Rejects with an axios error whose `response.data` is a Blob, since the
 * response type is `blob` for errors too. `downloadErrorMessage` reads it.
 */
export async function downloadAsset(
  assetId: string,
  onProgress?: DownloadProgress,
): Promise<DownloadedFile> {
  const response = await assetLibraryClient.get<Blob>(`/assets/${assetId}/download`, {
    responseType: 'blob',
    timeout: 0,
    onDownloadProgress: onProgress
      ? // Capped: if anything between here and the backend compresses the
        // response, `total` is the compressed Content-Length while `loaded`
        // counts decompressed bytes, and the raw fraction passes 1.
        (event) => onProgress(event.total ? Math.min(1, event.loaded / event.total) : null)
      : undefined,
  });
  const disposition = response.headers['content-disposition'] as string | undefined;
  return {
    blob: response.data,
    filename: filenameFromDisposition(disposition) ?? `${assetId}.download`,
  };
}

/**
 * A film's script, saved under the name it was uploaded as.
 *
 * Fetched through the client for the same reason as `downloadAsset`. The name
 * is the caller's (`script_filename`), not the header's: the header's plain
 * `filename` is an ASCII fallback, and the uploaded name may not be ASCII.
 */
export async function downloadScript(assetId: string, filename: string): Promise<DownloadedFile> {
  const response = await assetLibraryClient.get<Blob>(`/assets/${assetId}/script`, {
    responseType: 'blob',
    timeout: 0,
  });
  return { blob: response.data, filename };
}

/**
 * The most source images one zip may hold. Mirrors `MAX_DOWNLOAD_SELECTION` in
 * `apps/asset-library/backend/src/core/download.py`, which refuses more with a
 * 422; held here so the toolbar can say so before asking. Sized so the whole
 * download finishes inside Cloud Run's 300 s request timeout.
 */
export const MAX_DOWNLOAD_SELECTION = 20;

/**
 * Several published source images as one zip: each image, its sidecar JSON,
 * and a manifest.csv for the selection.
 *
 * The zip is streamed and has no Content-Length, so progress always reports
 * null and callers show an indeterminate spinner. Everything the backend can
 * refuse (unknown ids, expired or ineligible assets) it refuses before
 * streaming, as a 404 or 409 whose `detail.message` says how many; a file
 * that fails mid-download is named inside the zip instead.
 *
 * No timeout, for the same reason as `downloadAsset`.
 */
export async function downloadSelection(
  assetIds: string[],
  onProgress?: DownloadProgress,
): Promise<DownloadedFile> {
  const response = await assetLibraryClient.post<Blob>(
    '/assets/download',
    { asset_ids: assetIds },
    {
      responseType: 'blob',
      timeout: 0,
      onDownloadProgress: onProgress
        ? (event) => onProgress(event.total ? Math.min(1, event.loaded / event.total) : null)
        : undefined,
    },
  );
  const disposition = response.headers['content-disposition'] as string | undefined;
  return {
    blob: response.data,
    filename: filenameFromDisposition(disposition) ?? 'source-images.zip',
  };
}
